/**
 * Dialog
 * <uo-dialog>
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, query, property } from 'lit/decorators.js';

// Import animations
import { fadeOut } from '@automator/animations';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-dialog' )
export class AutomatorDialog extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Get wrapper
	@query( '.wrapper' ) accessor $wrapper;

	// Properties
	@property( { type: String } ) accessor heading;

	@property( { type: String } ) accessor size; // xsmall, small, regular, large, xlarge

	@property( { type: Boolean } ) accessor open;

	// Defines if the dialog can only be forced by invoking .close()
	// If set to true, the dialog will be closed when pressing Esc or when clicking outside the box
	@property( { type: Boolean, attribute: 'force-manual-close' } ) accessor forceManualClose = false;

	@property( { type: Boolean, attribute: 'no-header' } ) accessor noHeader = false;

	@property( { type: Boolean, attribute: 'no-footer' } ) accessor noFooter = false;

	@property( { type: Boolean, attribute: 'no-padding' } ) accessor noPadding = false;

	// eslint-disable-next-line require-jsdoc
	constructor() {
		super();

		// [internal] Define the action attribute
		this.actionAttribute = 'dialog-action';
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div 
				class="wrapper"
				?open=${ this.open }

				@click=${ this._handleClickOutside }
			>
				<div 
					class="dialog"
					size="${ this.size }"
					?no-padding=${ this.noPadding }
				>
					${ ! this.noHeader ? html`<div class="dialog-heading">${ unsafeHTML( this.heading ) }</div>` : '' }

					<div class="dialog-content">
						<slot></slot>
					</div>

					${ ! this.noFooter ? html`<div class="dialog-footer" @click=${ this._handleClick }>
						<slot name="secondary-action"></slot>
						<slot name="primary-action"></slot>
					</div>` : '' }
				</div>
			</div>
		`;
	}

	/**
	 * Disable scroll on body when the dialog is added
	 *
	 * @return {undefined}
	 */
	connectedCallback() {
		super.connectedCallback();

		// Disable scroll on body element
		document.body.classList.add( 'uap-disable-scroll' );

		// Remove focus from current focused element (if there is one)
		// We need to do this if we want to use autofocus on fields inside the dialog
		if ( document.activeElement ) {
			document.activeElement.blur();
		}

		// Remove when pressing escape
		if ( ! this.forceManualClose ) {
			document.body.addEventListener( 'keydown', this._handleEscapeKey );
		}
	}

	/**
	 * Enable scroll on body when the dialog is added
	 *
	 * @return {undefined}
	 */
	disconnectedCallback() {
		super.connectedCallback();

		// Remove event that listens for the escape key
		document.body.removeEventListener( 'keydown', this._handleEscapeKey );

		// Disable scroll on body element
		document.body.classList.remove( 'uap-disable-scroll' );
	}

	/**
	 * Closes and destroys the modal
	 *
	 * @return {undefined}
	 */
	close() {
		// Fade out container
		fadeOut( this, () => {
			// Delete the node
			this.parentNode.removeChild( this );
		} );

		// Enable scroll on body
		document.body.classList.remove( 'uap-disable-scroll' );

		this.dispatchEvent( new Event( 'uap-dialog-close' ) );
	}

	/**
	 * Handles the action cancellation
	 *
	 * @return {undefined}
	 */
	cancel() {
		// Close the modal
		this.close();
	}

	/**
	 * Handles closing the dialog by pressing "Escape"
	 *
	 * @param {KeyboardEvent} event
	 */
	_handleEscapeKey( event ) {
		if ( event.key === 'Escape' ) {
			try {
				// Find the dialog
				const $dialogs = document.querySelectorAll( 'uo-dialog' );

				if ( $dialogs ) {
					const $lastDialog = $dialogs[ $dialogs.length - 1 ];
					$lastDialog.close();
				}
			} catch ( e ) {
				console.warn( e );
			}
		}
	}

	/**
	 * Handles clicks outside the box and evaluates whether the it should close the dialog
	 *
	 * @param {PointerEvent} event
	 */
	_handleClickOutside( event ) {
		// Check if the dialog can only be closed when manually invoking .close()
		if ( this.forceManualClose ) {
			return;
		}

		// Check if the user is clicking any other element but the wrapper
		if ( event.target !== this.$wrapper ) {
			return;
		}

		// Otherwise (so if it's clicking the wrapper), close the dialog
		this.cancel();
	}

	/**
	 * Listens and handles clicks on the actions
	 *
	 * @param {PointerEvent} event The click event
	 * @return {undefined}
	 */
	_handleClick( event ) {
		// Check if the user is trying to cancel the action
		if ( event.target.closest( `[${ this.actionAttribute }="close"]` ) ) {
			// Cancel the action
			this.cancel();
		}
	}
}
